import { InfoOutlined } from "@mui/icons-material";
import { Box, Link, Tooltip, Typography } from "@mui/material";
import type React from "react";
import type { Dispatch, SetStateAction } from "react";
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { SystemSwitch } from "../../../../components/UIToolkit/Clickable/SystemSwitch";
import type { ValidationTextFieldProps } from "../../../../components/UIToolkit/Inputs/SystemValidationTextField";
import SystemButton from "../../../../components/UIToolkit/SystemButton/SystemButton";
import { useAuth } from "../../../../contexts/AuthProvider";
import { useHospitalData } from "../../../../contexts/HospitalDataProvider";
import { useSnackbar } from "../../../../contexts/SnackbarContext";
import { useDebouncedValue } from "../../../../hooks/useDebouncedValue";
import { useIsMobile } from "../../../../hooks/useMobile";
import { useFormatPersonName } from "../../../../hooks/useNameFormat";
import { usePhoneValidation } from "../../../../hooks/usePhoneValidation";
import { STATUS } from "../../../../theme/tokens";
import { trackErrorEvent, trackEvent } from "../../../../utils/analytics";
import { type NurseWithExternalData, ProfileMode } from "../../../../utils/models";
import { cleanEmail, guessAndFormatPhoneNumber, isValidEmail, validatePhoneNumber } from "../../../../utils/phoneAndEmailUtils";
import { isAgencyNurse } from "../../../../utils/utilFunctions";
import { useWelcomeReset } from "../../hooks";
import { useGetNurseNameExists, useRemoveNurse } from "../hooks";

type Props = {
	nurse?: NurseWithExternalData;
	setNurseData: React.Dispatch<React.SetStateAction<NurseWithExternalData>>;
	departmentId: string;
	profileMode: ProfileMode;
	refetchProfileRequested: () => void;
	createUserWhenCreatingNurse: boolean;
	setCreateUserWhenCreatingNurse: Dispatch<SetStateAction<boolean>>;
};

export function partialNameValidationCallback(value: string): boolean {
	return value.trimStart().length > 0;
}

export function phoneNumberValidationCallback(value: string, allowEmpty = true): boolean {
	return (value.length === 0 && allowEmpty) || validatePhoneNumber(value).isValid;
}

export function emailValidationCallback(value: string, allowEmpty = true): boolean {
	return (value.length === 0 && allowEmpty) || isValidEmail(value);
}

export const useGeneralProfileDetailsState = ({
	nurse,
	setNurseData,
	departmentId,
	refetchProfileRequested,
	createUserWhenCreatingNurse,
	setCreateUserWhenCreatingNurse,
	profileMode,
}: Props) => {
	const { t } = useTranslation();
	const { userInfo } = useAuth();
	const { chosenDepartment, refreshDepartmentData, refreshNursesData } = useHospitalData();
	const formatPersonName = useFormatPersonName();

	const handleImageEditClick = useCallback(() => {
		trackEvent("edit_profile_image_clicked", {
			nurse_id: nurse?.id,
		});
	}, [nurse?.id]);

	const partialNameValidation = useMemo<ValidationTextFieldProps>(
		() => ({
			helperText: {
				valid: "Looks good!",
				invalid: t("oops.invalid.fullName"),
			},
			performValidation: "onlyAfterInitialChange",
			validationCallback: partialNameValidationCallback,
			showValidWhenValid: "never",
		}),
		[t],
	);

	const onFirstNameChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
			const value = e.target.value.trimStart();
			setNurseData((prev) => ({
				...prev,
				firstName: value,
			}));
		},
		[setNurseData],
	);

	const onLastNameChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
			const value = e.target.value.trimStart();
			setNurseData((prev) => ({
				...prev,
				lastName: value,
			}));
		},
		[setNurseData],
	);

	const firstName = useMemo(() => {
		return nurse?.firstName ?? "";
	}, [nurse?.firstName]);

	const lastName = useMemo(() => {
		return nurse?.lastName ?? "";
	}, [nurse?.lastName]);

	const phoneValidation = usePhoneValidation();

	const onPhoneChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
			const currentPhone = e.target.value.trim();
			const guessedPhoneNumber = guessAndFormatPhoneNumber(currentPhone);
			setNurseData((prev) => ({
				...prev,
				phoneNumber: guessedPhoneNumber.formatted,
			}));
		},
		[setNurseData],
	);

	const phoneNumber = useMemo(() => {
		return nurse?.phoneNumber ?? "";
	}, [nurse?.phoneNumber]);

	const emailValidation = useMemo<ValidationTextFieldProps>(
		() => ({
			validationCallback: emailValidationCallback,
			performValidation: "onlyAfterInitialChange",
			helperText: {
				invalid: t("oops.invalid.email"),
			},
			showValidWhenValid: "never",
		}),
		[t],
	);
	const emailAddress = useMemo(() => {
		return nurse?.email ?? "";
	}, [nurse?.email]);
	const onEmailChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
			const currentEmail = e.target.value;
			setNurseData((prev) => ({
				...prev,
				email: cleanEmail(currentEmail),
			}));
		},
		[setNurseData],
	);

	const { showSnackbar } = useSnackbar();
	const { showErrorSnackbar } = useHospitalData();
	const { sendWelcomeReset, error, isError, isLoading } = useWelcomeReset();
	const shouldCreateUser = useMemo(() => !nurse?.userId, [nurse]);

	useEffect(() => {
		if (isError) {
			const isReset = !shouldCreateUser;
			trackErrorEvent("create_or_reset_user_error", {
				is_reset: isReset,
				nurse_id: nurse?.id,
				email_orPhoneNumber: nurse?.email || nurse?.phoneNumber,
				error: error,
			});
			if (isReset) {
				showErrorSnackbar(t("editNurse.sendResetPassword.fail"), {
					action: "failed to reset password",
					nurse_id: nurse?.id,
					emailOrPhoneNumber: nurse?.email || nurse?.phoneNumber,
				});
			} else {
				showErrorSnackbar(t("editNurse.sendWelcomeEmail.fail"), {
					action: "failed to send welcome email",
					nurse_id: nurse?.id,
				});
			}
		}
	}, [error, isError, t, showErrorSnackbar, shouldCreateUser, nurse?.id, nurse?.email, nurse?.phoneNumber]);

	const handleCreateUserOrResendLinkClicked = useCallback(async () => {
		if (shouldCreateUser && nurse) {
			const result = await sendWelcomeReset({
				isReset: false,
				nurseId: nurse.id,
				shouldCreateUser,
				departmentId: departmentId,
			});
			if (result) {
				showSnackbar(t(nurse.email ? "editNurse.sendWelcomeEmail.success" : "editNurse.sendWelcomeSms.success"), "success");
			}
			refetchProfileRequested();
		} else if (nurse) {
			const result = await sendWelcomeReset({
				isReset: true,
				nurseId: nurse.id,
				shouldCreateUser,
				departmentId,
				emailOrPhoneNumber: nurse.email || nurse.phoneNumber,
			});
			if (result) {
				showSnackbar(t("editNurse.sendResetPassword.success"), "success");
			}
		}
	}, [nurse, shouldCreateUser, departmentId, sendWelcomeReset, refetchProfileRequested, showSnackbar, t]);

	const handleSwitchChange = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			trackEvent("profile_create_user_switch_changed", {
				nurse_id: nurse?.id,
				new_value: event.target.checked,
			});
			const isChecked = event.target.checked;
			setCreateUserWhenCreatingNurse(isChecked);
		},
		[setCreateUserWhenCreatingNurse, nurse?.id],
	);

	const isThereAnIssueCreatingUser = useMemo(
		() =>
			shouldCreateUser &&
			(createUserWhenCreatingNurse || profileMode !== ProfileMode.NEW) &&
			(!nurse || !(phoneNumberValidationCallback(phoneNumber, false) || emailValidationCallback(emailAddress, false))),
		[shouldCreateUser, createUserWhenCreatingNurse, profileMode, nurse, emailAddress, phoneNumber],
	);
	const { isMobile } = useIsMobile();
	const [openUserCreationTooltip, setOpenUserCreationTooltip] = useState(false);
	const toggleUserCreationTooltip = useCallback(() => {
		setOpenUserCreationTooltip((prev) => !prev);
	}, []);

	useEffect(() => {
		if (openUserCreationTooltip) {
			/**
			 * Auto close the tooltip
			 */
			setTimeout(() => {
				setOpenUserCreationTooltip(false);
			}, 4500);
		}
	}, [openUserCreationTooltip]);

	const isCreateUserDisabled = useMemo(() => nurse?.employmentType && isAgencyNurse(nurse.employmentType), [nurse?.employmentType]);

	const createUserSendResetButtonRef = useRef<HTMLButtonElement>(null);

	const [createUserSendResetButtonRefWidth, setCreateUserSendResetButtonRefWidth] = useState(0);

	const createUserSendResetButton = useMemo(
		() =>
			profileMode !== ProfileMode.EDIT ? (
				<Box
					sx={{
						display: "flex",
						alignItems: "center",
						justifyContent: isMobile ? "start" : "center",
					}}
				>
					{profileMode === ProfileMode.VIEW ? (
						<SystemButton
							sx={{
								fontSize: {
									xs: "8px",
									md: "14px",
								},
								textWrap: "wrap",
								width: {
									xs: "50px",
									mediumPhone: "80px",
									md: "200px",
								},
							}}
							ref={createUserSendResetButtonRef}
							buttonVariant={"primary"}
							onClick={handleCreateUserOrResendLinkClicked}
							disabled={isThereAnIssueCreatingUser}
							loading={isLoading}
						>
							{shouldCreateUser ? t("editNurse.sendWelcomeEmail.createUserButton") : t("editNurse.sendResetPassword.button")}
						</SystemButton>
					) : profileMode === ProfileMode.NEW ? (
						<SystemSwitch
							onChange={handleSwitchChange}
							checked={createUserWhenCreatingNurse}
							label={t("editNurse.sendWelcomeEmail.createUserButton")}
							disabled={isCreateUserDisabled}
						/>
					) : null}
					{shouldCreateUser && (
						<Tooltip
							open={isMobile ? openUserCreationTooltip : undefined}
							disableHoverListener={!isThereAnIssueCreatingUser}
							title={
								<Typography
									style={{
										fontSize: "12px",
									}}
								>
									{t("editNurse.sendWelcomeEmail.requirements")}
								</Typography>
							}
						>
							<InfoOutlined
								onClick={toggleUserCreationTooltip}
								style={{
									color: "red",
									opacity: isThereAnIssueCreatingUser ? 1 : 0,
								}}
								sx={{ ml: 1 }}
							/>
						</Tooltip>
					)}
				</Box>
			) : null,
		[
			profileMode,
			handleCreateUserOrResendLinkClicked,
			t,
			shouldCreateUser,
			createUserWhenCreatingNurse,
			handleSwitchChange,
			isThereAnIssueCreatingUser,
			isLoading,
			isCreateUserDisabled,
			toggleUserCreationTooltip,
			openUserCreationTooltip,
			isMobile,
		],
	);

	useLayoutEffect(() => {
		if (createUserSendResetButtonRef.current) {
			setCreateUserSendResetButtonRefWidth(createUserSendResetButtonRef.current.offsetWidth);
		}
	}, []);

	const debouncedFirstName = useDebouncedValue(firstName, 500);
	const debouncedLastName = useDebouncedValue(lastName, 500);
	const { data: nurseNameExistsData, isLoading: isCheckingNurseName } = useGetNurseNameExists({
		department_id: departmentId,
		first_name: debouncedFirstName,
		last_name: debouncedLastName,
		nurse_id: nurse?.id,
		isProfileEditable: profileMode !== ProfileMode.VIEW,
	});
	const nurseNameExistsWarning = useMemo(() => {
		if (!nurseNameExistsData) {
			return null;
		}
		const data = nurseNameExistsData as unknown as { exists: boolean; nurse_id: number | null };
		if (!data.exists) {
			return null;
		}
		const fullName = formatPersonName({ firstName: debouncedFirstName, lastName: debouncedLastName });
		return data.exists && data.nurse_id && (!nurse?.id || data.nurse_id !== nurse.id) ? (
			<>
				<Link
					component="button"
					type="button"
					onClick={() => {
						window.open(`/${chosenDepartment.department_id}/nurse/${data.nurse_id}`, "_blank");
					}}
					sx={{
						color: STATUS.WARNING.main,
						textDecoration: "underline",
						cursor: "pointer",
						background: "none",
						border: "none",
						padding: 0,
						font: "inherit",
						fontWeight: 700,
						"&:hover": {
							textDecoration: "underline",
						},
					}}
				>
					'{fullName}'
				</Link>

				{" already exists in your department."}
			</>
		) : null;
	}, [nurseNameExistsData, debouncedFirstName, debouncedLastName, chosenDepartment.department_id, nurse, formatPersonName]);

	const { removeNurse, isRemovingNurse } = useRemoveNurse();

	const handleRemoveNurseClick = useCallback(async (): Promise<void> => {
		if (isRemovingNurse || !nurse) {
			return;
		}

		trackEvent("profile_remove_nurse_confirm", {
			nurseId: nurse.id,
			nurseName: `${nurse.firstName} ${nurse.lastName}`,
			departmentId: chosenDepartment.department_id,
		});

		try {
			await removeNurse({
				departmentId: chosenDepartment.department_id,
				nurseId: nurse.id,
			});
			await refreshDepartmentData(chosenDepartment.department_id, false);
			await refreshNursesData(chosenDepartment.department_id);
			refetchProfileRequested();
		} catch {}
	}, [nurse, isRemovingNurse, chosenDepartment, removeNurse, refreshDepartmentData, refreshNursesData, refetchProfileRequested]);

	const isSupportUser = userInfo.support;

	return {
		firstName,
		lastName,
		partialNameValidation,
		onFirstNameChange,
		onLastNameChange,
		phoneNumber,
		phoneValidation,
		onPhoneChange,
		emailAddress,
		emailValidation,
		onEmailChange,
		handleImageEditClick,
		createUserSendResetButton,
		isThereAnIssueCreatingUser,
		nurseNameExistsWarning,
		isCheckingNurseName,
		handleRemoveNurseClick,
		isRemovingNurse,
		isSupportUser,
		createUserSendResetButtonRefWidth,
	};
};
